import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import './StudentSidebar.css';
import {
  BookOpen,
  BarChart3,
  Calendar,
  Trophy,
  Settings,
  User,
  Bell,
  ShoppingBag,
  Brain,
  Target,
  MessageCircle,
  LogOut,
  Home,
  ChevronsLeft,
  ChevronsRight,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Badge } from '../../ui/badge';
import { getStudentEnrollments } from '../../../services/elearningStudentService';
import { getDomainFilterParams } from '../../../hooks/useDomainQuery';

interface StudentSidebarProps {
  studentProfile: any;
  onLogout: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  // Nouvelle prop pour indiquer si on doit appliquer une marge au contenu principal
  noMargin?: boolean;
}

const StudentSidebar: React.FC<StudentSidebarProps> = ({
  studentProfile,
  onLogout,
  isCollapsed = false,
  onToggleCollapse,
  noMargin = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [coursesCount, setCoursesCount] = useState(0);

  // Détecter si l'écran est de taille mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px est généralement la limite pour les appareils mobiles
    };
    
    // Vérifier au chargement initial
    checkIfMobile();
    
    // Ajouter un écouteur d'événement pour les changements de taille d'écran
    window.addEventListener('resize', checkIfMobile);
    
    // Nettoyer l'écouteur d'événement
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Charger le nombre de cours de l'étudiant
  useEffect(() => {
    const loadCoursesCount = async () => {
      if (studentProfile?.id) {
        try {
          const { tenantId } = getDomainFilterParams();
          if (tenantId) {
            const enrollments = await getStudentEnrollments(studentProfile.id, tenantId);
            setCoursesCount(enrollments.length);
          }
        } catch (error) {
          console.error('Erreur lors du chargement du nombre de cours:', error);
          setCoursesCount(0);
        }
      }
    };

    loadCoursesCount();
  }, [studentProfile?.id]);

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Tableau de bord',
      icon: Home,
      path: '/elearning/student/dashboard',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      id: 'courses',
      label: 'Mes Cours',
      icon: BookOpen,
      path: '/elearning/student/courses',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      badge: coursesCount > 0 ? coursesCount.toString() : undefined
    },
    {
      id: 'progress',
      label: 'Progression',
      icon: BarChart3,
      path: '/elearning/student/progress',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    {
      id: 'calendar',
      label: 'Planning',
      icon: Calendar,
      path: '/elearning/student/calendar',
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10'
    },
    {
      id: 'achievements',
      label: 'Réussites',
      icon: Trophy,
      path: '/elearning/student/achievements',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10'
    },
    {
      id: 'quiz',
      label: 'Quiz & Tests',
      icon: Brain,
      path: '/elearning/student/quiz',
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10'
    },
    {
      id: 'goals',
      label: 'Objectifs',
      icon: Target,
      path: '/elearning/student/goals',
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10'
    },
    {
      id: 'orders',
      label: 'Mes Commandes',
      icon: ShoppingBag,
      path: '/elearning/student/orders',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    }
  ];

  const bottomMenuItems = [
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      path: '/elearning/student/notifications',
      color: 'text-gray-500',
      badge: '2'
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: MessageCircle,
      path: '/elearning/student/messages',
      color: 'text-gray-500'
    },
    {
      id: 'profile',
      label: 'Profil',
      icon: User,
      path: '/elearning/student/profile',
      color: 'text-gray-500'
    }
  ];

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  // Fonction pour naviguer et fermer le menu mobile si nécessaire
  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Bouton du menu hamburger pour mobile */}
      {isMobile && (
        <div className="fixed top-4 right-4 z-[60]">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-full bg-black/80 backdrop-blur-sm border border-white/10 shadow-lg"
            aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>
      )}
      
      {/* Overlay semi-transparent pour mobile */}
      {isMobile && isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar pour desktop ou menu déroulant pour mobile */}
      <AnimatePresence>
        {(!isMobile || (isMobile && isMobileMenuOpen)) && (
          <motion.div
            initial={isMobile ? { x: -300 } : { x: -300 }}
            animate={isMobile ? { x: 0 } : { x: 0 }}
            exit={isMobile ? { x: -300 } : { x: -300 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={`
              bg-black/80 backdrop-blur-sm border-white/10 shadow-lg
              ${isMobile 
                ? 'fixed top-0 left-0 bottom-0 w-[85%] max-w-[300px] overflow-y-auto border-r z-50' 
                : `fixed top-0 left-0 h-full border-r z-40 ${isCollapsed ? 'w-20' : 'w-72'}`
              }
              transition-all duration-300 ease-in-out
              pointer-events-auto
              sidebar-component
            `}
          >
            {/* Bouton de toggle pour la sidebar (uniquement sur desktop) */}
            {!isMobile && (
              <div className="flex justify-end p-2">
                <button 
                  onClick={onToggleCollapse}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
                  aria-label={isCollapsed ? "Étendre la sidebar" : "Rétracter la sidebar"}
                >
                  {isCollapsed ? (
                    <ChevronsRight className="h-5 w-5 text-gray-300" />
                  ) : (
                    <ChevronsLeft className="h-5 w-5 text-gray-300" />
                  )}
                </button>
              </div>
            )}
            
            {/* Header avec profil */}
            <div className="p-6 border-b border-white/10">
              <div className={`flex items-center ${isCollapsed && !isMobile ? 'justify-center' : 'space-x-3'}`}>
                <Avatar className="h-12 w-12">
                  <AvatarImage src={studentProfile?.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                    {studentProfile?.first_name?.[0]}{studentProfile?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                {(!isCollapsed || isMobile) && (
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white">
                      Salut, {studentProfile?.full_name} 👋
                    </h3>
                    <p className="text-sm text-gray-300 truncate">
                      {studentProfile?.email}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Menu principal */}
            <div className={`${isMobile ? 'py-4' : 'flex-1'} py-6 px-3 overflow-y-auto`}>
              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.path);
                  
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        w-full flex items-center space-x-3 px-3 py-3 rounded-xl transition-all duration-200
                        ${isActive 
                          ? `${item.bgColor} ${item.color} shadow-sm` 
                          : 'text-gray-300 hover:bg-white/5'
                        }
                        ${isCollapsed && !isMobile ? 'justify-center' : ''}
                      `}
                    >
                      <Icon className={`h-5 w-5 ${isActive ? item.color : ''}`} />
                      {(!isCollapsed || isMobile) && (
                        <>
                          <span className="font-medium flex-1 text-left">{item.label}</span>
                          {item.badge && (
                            <Badge variant="secondary" className="h-5 px-2 text-xs">
                              {item.badge}
                            </Badge>
                          )}
                        </>
                      )}
                    </motion.button>
                  );
                })}
              </nav>
            </div>

            {/* Menu du bas */}
            <div className="border-t border-white/10 p-3">
              <nav className="space-y-1">
                {bottomMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.path);
                  
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200
                        ${isActive 
                          ? 'bg-white/10 text-white' 
                          : 'text-gray-300 hover:bg-white/5'
                        }
                        ${isCollapsed && !isMobile ? 'justify-center' : ''}
                      `}
                    >
                      <Icon className="h-4 w-4" />
                      {(!isCollapsed || isMobile) && (
                        <>
                          <span className="text-sm flex-1 text-left">{item.label}</span>
                          {item.badge && (
                            <Badge variant="destructive" className="h-4 px-1.5 text-xs">
                              {item.badge}
                            </Badge>
                          )}
                        </>
                      )}
                    </motion.button>
                  );
                })}
              </nav>

              {/* Bouton de déconnexion */}
              <motion.button
                onClick={onLogout}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  w-full flex items-center space-x-3 px-3 py-2 mt-3 rounded-lg transition-all duration-200
                  text-red-400 hover:bg-red-900/20
                  ${isCollapsed && !isMobile ? 'justify-center' : ''}
                `}
              >
                <LogOut className="h-4 w-4" />
                {(!isCollapsed || isMobile) && <span className="text-sm">Déconnexion</span>}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StudentSidebar;
